/**
 * The shapes of the data dictionary (ADR central-data-model,
 * docs/DATA-MODEL.md): what every field and every code means, built in (in
 * code) and edited by Admins as overrides.
 *
 * Imports only the entity list (a leaf), so any part of the app can read a
 * label without an import cycle.
 */
import { z } from 'zod'
import { DATA_ENTITIES } from './content/entities.ts'

export { DATA_ENTITIES, type DataEntity } from './content/entities.ts'
export const dataEntitySchema = z.enum(DATA_ENTITIES)

/**
 * How sensitive a field is — who may read it. Set in code and changed only in
 * a reviewed release, never on a screen: it is what an AI app's access to the
 * field is decided by (docs/DATA-MODEL.md → "Sensitivity").
 *  - general     anyone in the workspace;
 *  - financial   money: prices, totals, rates;
 *  - personal    a person's details: names, emails, phones, addresses;
 *  - operations  how the business runs internally: notes, ids, bookkeeping.
 */
export const SENSITIVITY_TAGS = ['general', 'financial', 'personal', 'operations'] as const
export type SensitivityTag = (typeof SENSITIVITY_TAGS)[number]

export const FIELD_KINDS = [
  'text',
  'code',
  'date',
  'datetime',
  'money',
  'count',
  'number',
  'flag',
  'id',
  'person',
] as const
export type FieldKind = (typeof FIELD_KINDS)[number]

/**
 * How sure a code's meaning is. A code declared in this app's own code is
 * `confirmed`; one that arrives from another system is only as sure as the
 * evidence for it.
 */
export const CODE_CONFIDENCES = ['confirmed', 'strong', 'unsure'] as const
export type CodeConfidence = (typeof CODE_CONFIDENCES)[number]
export const codeConfidenceSchema = z.enum(CODE_CONFIDENCES)

/** A field as built in. */
export interface FieldDef {
  entity: (typeof DATA_ENTITIES)[number]
  /** The app's own key for the field — the same everywhere (records, filters, AI apps). */
  key: string
  label: string
  description: string
  kind: FieldKind
  /** e.g. "dollars", "hours", "people" — when a number has one. */
  unit?: string
  tag: SensitivityTag
}

/** A code as built in. */
export interface CodeDef {
  entity: (typeof DATA_ENTITIES)[number]
  /** The coded field's key, e.g. `status`. */
  field: string
  /** Exactly as stored. */
  value: string
  /** A short name, e.g. "In progress". */
  label: string
  meaning: string
  confidence: CodeConfidence
  evidence?: string
}

/** Codes described beyond the built-ins — a bound, so the list never grows without limit. */
export const MAX_CODE_OVERRIDES = 2000

const LABEL_MAX = 60
const DESCRIPTION_MAX = 1000
const VALUE_MAX = 120
const EVIDENCE_MAX = 500

/** A field's id: `item.status`. */
export const fieldIdSchema = z
  .string()
  .max(120)
  .regex(/^[a-zA-Z]+\.[a-zA-Z0-9]+$/, 'A field id is entity.key')

/** What an Admin changed on a field. `null` = back to the built-in. */
export const fieldOverrideSchema = z.object({
  label: z.string().trim().min(1).max(LABEL_MAX).nullable(),
  description: z.string().trim().min(1).max(DESCRIPTION_MAX).nullable(),
})
export type FieldOverrideValues = z.infer<typeof fieldOverrideSchema>

/** A stored field override, as the repository returns it. */
export const fieldOverrideRowSchema = fieldOverrideSchema.extend({
  id: z.string(),
  updatedAt: z.iso.datetime(),
  updatedBy: z.string().nullable(),
})
export type FieldOverride = z.infer<typeof fieldOverrideRowSchema>

/** What an Admin changed on a code — or the whole description of a code the built-ins don't know. */
export const codeOverrideSchema = z.object({
  label: z.string().trim().min(1).max(LABEL_MAX).nullable(),
  meaning: z.string().trim().min(1).max(DESCRIPTION_MAX).nullable(),
  confidence: codeConfidenceSchema.nullable(),
  evidence: z.string().trim().min(1).max(EVIDENCE_MAX).nullable(),
})
export type CodeOverrideValues = z.infer<typeof codeOverrideSchema>

export const codeOverrideRowSchema = codeOverrideSchema.extend({
  id: z.string(),
  updatedAt: z.iso.datetime(),
  updatedBy: z.string().nullable(),
})
export type CodeOverride = z.infer<typeof codeOverrideRowSchema>

export const updateFieldInputSchema = z.object({ id: fieldIdSchema, values: fieldOverrideSchema })
export const updateCodeInputSchema = z.object({
  fieldId: fieldIdSchema,
  value: z.string().min(1).max(VALUE_MAX),
  values: codeOverrideSchema,
})
export type UpdateFieldInput = z.infer<typeof updateFieldInputSchema>
export type UpdateCodeInput = z.infer<typeof updateCodeInputSchema>

/** A field as everyone reads it: built in, with any Admin edit applied. */
export const effectiveFieldSchema = z.object({
  id: z.string(),
  entity: dataEntitySchema,
  key: z.string(),
  label: z.string(),
  description: z.string(),
  kind: z.enum(FIELD_KINDS),
  unit: z.string().nullable(),
  tag: z.enum(SENSITIVITY_TAGS),
  /** The built-in words, so the screen can say what was changed and reset it. */
  builtIn: z.object({ label: z.string(), description: z.string() }),
  edited: z.boolean(),
  updatedAt: z.iso.datetime().nullable(),
  updatedBy: z.string().nullable(),
})
export type EffectiveField = z.infer<typeof effectiveFieldSchema>

export const effectiveCodeSchema = z.object({
  id: z.string(),
  fieldId: z.string(),
  value: z.string(),
  label: z.string().nullable(),
  meaning: z.string().nullable(),
  confidence: codeConfidenceSchema.nullable(),
  evidence: z.string().nullable(),
  builtIn: z
    .object({
      label: z.string(),
      meaning: z.string(),
      confidence: codeConfidenceSchema,
      evidence: z.string().nullable(),
    })
    .nullable(),
  edited: z.boolean(),
  updatedAt: z.iso.datetime().nullable(),
  updatedBy: z.string().nullable(),
})
export type EffectiveCode = z.infer<typeof effectiveCodeSchema>

export const dictionarySchema = z.object({
  entities: z.array(
    z.object({ entity: dataEntitySchema, label: z.string(), description: z.string() }),
  ),
  fields: z.array(effectiveFieldSchema),
  codes: z.array(effectiveCodeSchema),
  /** The fields whose values are codes, in the order the screen lists them. */
  codedFields: z.array(z.string()),
})
export type DataDictionaryView = z.infer<typeof dictionarySchema>

/** Refused edits — the router turns these into plain messages. */
export type DictionaryRefusalReason = 'unknown-field' | 'not-coded' | 'too-many'
export class DictionaryRefusal extends Error {
  readonly reason: DictionaryRefusalReason
  constructor(reason: DictionaryRefusalReason, message: string) {
    super(message)
    this.reason = reason
  }
}
